import { API_BASE_URL, PUBLIC_SITE_URL } from "@/config/env";

import { KAKAO_DEFAULT_OG_IMAGE } from "./kakao.config";

const publicSiteOrigin = (): string =>
  PUBLIC_SITE_URL.replace(/\/+$/, "") || "https://www.thehope.one";

export const kakaoFallbackImageUrl = (): string =>
  `${publicSiteOrigin()}${KAKAO_DEFAULT_OG_IMAGE}`;

const isLocalhostUrl = (url: string): boolean =>
  /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(url);

/** Kakao custom templates require absolute HTTPS URLs reachable without auth. */
const toKakaoHttpsUrl = (url: string): string => {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;

  if (trimmed.startsWith("//")) {
    return `https:${trimmed}`;
  }

  if (/^http:\/\//i.test(trimmed)) {
    return trimmed.replace(/^http:\/\//i, "https://");
  }

  return trimmed;
};

const apiMediaOrigin = (): string => {
  const raw = API_BASE_URL.trim();
  if (!raw) return "";
  try {
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return new URL(withScheme).origin;
  } catch {
    return "";
  }
};

const absolutizeMediaUrl = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("//")) {
    return toKakaoHttpsUrl(trimmed.startsWith("//") ? `https:${trimmed}` : trimmed);
  }

  if (trimmed.startsWith("blob:") || trimmed.startsWith("data:")) {
    return "";
  }

  const apiOrigin = apiMediaOrigin();
  if (apiOrigin) {
    const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return toKakaoHttpsUrl(`${apiOrigin}${path}`);
  }

  if (typeof window === "undefined") {
    const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return `${publicSiteOrigin()}${path}`;
  }

  const onLocalhost = /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
  const origin = onLocalhost ? publicSiteOrigin() : window.location.origin.replace(/\/+$/, "");
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${origin}${path}`;
};

/** Kakao requires a publicly reachable absolute HTTPS image URL. */
export const resolveKakaoImageUrl = (imageUrl?: string | null): string => {
  const raw = imageUrl?.trim();
  if (!raw) {
    return kakaoFallbackImageUrl();
  }

  let resolved = absolutizeMediaUrl(raw);

  if (!resolved || isLocalhostUrl(resolved)) {
    resolved = kakaoFallbackImageUrl();
  }

  resolved = toKakaoHttpsUrl(resolved);

  if (!/^https:\/\/.+/i.test(resolved)) {
    return kakaoFallbackImageUrl();
  }

  return resolved;
};
